import React from 'react'

import Loader from '@/app/component/atom/loader'
import { useCheckInvitation } from '@/app/module/auth/hooks/use-check-invitation'
import { logoutUser } from '@/app/module/auth/store/session.slice'
import { useAppDispatch } from '@/app/store/redux-hooks'

import Invalid from './invalid'
import LoggedIn from './logged-in'
import LoggedInSame from './logged-in-same'
import LoggedOutExisting from './logged-out-existing'
import LoggedOutNew from './logged-out-new'
import { InvitationResponse } from './types'

type Props = {
  invitationToken: string
  url: string
}

const Invitation: React.FC<Props> = ({ invitationToken, url }) => {
  const dispatch = useAppDispatch()

  const [checkInvitation, { isLoading }] = useCheckInvitation()
  const [invitationResponse, setInvitationResponse] = React.useState<InvitationResponse | null>(null)
  const [isInvalid, setIsInvalid] = React.useState(false)

  const handleInvitationCheck = React.useCallback(
    async (token: string) => {
      if (!token) {
        return
      }

      const response = await checkInvitation({ invitationToken: token })
      if (!response) {
        setInvitationResponse(null)
        setIsInvalid(true)
        return
      }
      setInvitationResponse(response)
      setIsInvalid(false)
    },
    [checkInvitation],
  )

  React.useEffect(() => {
    handleInvitationCheck(invitationToken)
  }, [invitationToken, handleInvitationCheck])

  const handleLogout = React.useCallback(() => {
    dispatch(logoutUser())
    handleInvitationCheck(invitationToken)
  }, [invitationToken, dispatch, handleInvitationCheck])

  if (isLoading) {
    return <Loader />
  }

  if (isInvalid) {
    return <Invalid />
  }

  if (invitationResponse?.loggedInSame) {
    return <LoggedInSame invitationResponse={invitationResponse} invitationToken={invitationToken} />
  }

  if (invitationResponse?.loggedIn) {
    return <LoggedIn onLogout={handleLogout} />
  }

  if (invitationResponse?.existingUser) {
    return <LoggedOutExisting invitationResponse={invitationResponse} url={url} />
  }

  if (invitationResponse) {
    return <LoggedOutNew invitationResponse={invitationResponse} invitationToken={invitationToken} />
  }

  return null
}

export default Invitation
